import type { Nullable } from '@/types'
import { defineStore } from 'pinia'

export const AlertType = Object.freeze({
  Success: 'success',
  Error: 'error',
} as const)

type TAlertType = (typeof AlertType)[keyof typeof AlertType]

type AlertStoreState = {
  show: boolean
  alerts: Map<TAlertType, AlertDetails>
}

type AlertDetails = {
  __type: 'AlertDetails'
  type: TAlertType
  message?: string
  title?: string
  buttonText?: string
  callbackFn?: () => Promise<any>
}

export const useAlertStore = defineStore('alert', {
  state: (): AlertStoreState => ({
    show: false,
    alerts: new Map<TAlertType, AlertDetails>(),
  }),

  getters: {
    /**
     * Returns a default error alert object.
     *
     * @returns {AlertDetails}
     */
    defaultErrorAlert(): AlertDetails {
      return {
        __type: 'AlertDetails' as AlertDetails['__type'],
        type: 'error',
        message: '!',
        buttonText: 'Go back',
      }
    },

    /**
     * Returns a default success alert object.
     *
     * @returns {AlertDetails}
     */
    defaultSuccessAlert(): AlertDetails {
      return {
        __type: 'AlertDetails' as AlertDetails['__type'],
        type: 'success',
        title: 'Success!',
        buttonText: 'Go back',
      }
    },

    /**
     * Returns the next alert to display.
     *
     * @returns {Nullable<AlertDetails>}
     */
    nextAlert(): Nullable<AlertDetails> {
      const alertKeys = Array.from(this.alerts.keys())

      if (!alertKeys.length) {
        return null
      }

      const nextAlertKey = alertKeys[0]

      return this.alerts.get(nextAlertKey) as AlertDetails
    },
  },

  actions: {
    /**
     * Adds an alert dialog.
     *
     * @param {TAlertType} type
     * @param {AlertDetails} alertDetails
     * @param {boolean} immediateShow
     */
    addAlert(
      alertType: TAlertType,
      alertDetails: AlertDetails,
      immediateShow: boolean = true
    ) {
      const alertObject = {
        __type: 'AlertDetails' as AlertDetails['__type'],
        type: alertDetails.type,
        title:
          alertDetails?.title ??
          (alertDetails.type === 'error' ? 'Something went wrong' : undefined),
        message:
          alertDetails?.message ??
          (alertDetails.type === 'error'
            ? 'Error occurred. We are working hard to fix it!'
            : 'Success!'),
        buttonText: alertDetails?.buttonText ?? 'Go Back',
        callbackFn: alertDetails?.callbackFn ?? undefined,
      }

      this.alerts.set(alertType, alertObject)

      if (immediateShow) {
        this.showAlert()
      }
    },

    /**
     * Adds an error alert dialog.
     *
     * @param {AlertDetails} errorDetails
     * @param {boolean} immediateShow
     */
    addErrorAlert(
      errorDetails?: Omit<AlertDetails, 'type' | '__type'>,
      immediateShow: boolean = true
    ) {
      this.addAlert(
        AlertType.Error,
        { ...this.defaultErrorAlert, ...errorDetails },
        immediateShow
      )
    },

    /**
     * Adds a success alert dialog.
     *
     * @param {AlertDetails} successDetails
     * @param {boolean} immediateShow
     */
    addSuccessAlert(
      successDetails?: Omit<AlertDetails, 'type' | '__type'>,
      immediateShow: boolean = true
    ) {
      this.addAlert(
        AlertType.Success,
        { ...this.defaultSuccessAlert, ...successDetails },
        immediateShow
      )
    },

    /**
     * Shows the next alert in the list.
     */
    showAlert() {
      if (!this.alerts.size) {
        return
      }

      setTimeout(() => {
        this.show = true
      }, 100)
    },

    /**
     * Hides the alert and removes it from in the list.
     *
     * @param {TAlertType} alertType
     * @param {boolean} showNextAlert
     */
    hideAlert(alertType?: TAlertType, showNextAlert: boolean = true) {
      this.show = false

      if (!this.alerts.size) {
        return
      }

      if (alertType) {
        this.alerts.delete(alertType)
      } else {
        const alertKeys = Array.from(this.alerts.keys())
        const lastAlertKey = alertKeys[0]

        this.alerts.delete(lastAlertKey)
      }

      if (showNextAlert) {
        this.showAlert()
      }
    },
  },
})
